// feature-module/pages/login/Login.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authActions } from '../../../core/action/authActions';
import { companyActions } from '../../../core/action/companyActions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Mail, Lock, LogIn, Eye, EyeOff, User, Shield } from 'feather-icons-react/build/IconComponents';
import all_routes from '../../../Router/all_routes';
import '../../../style/css/login.css';

const MySwal = withReactContent(Swal);

const Login = () => {
    const navigate = useNavigate();
    const routes = all_routes;
    
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [companySettings, setCompanySettings] = useState({});
    
    useEffect(() => {
        loadCompanySettings();
        
        if (authActions.isAuthenticated()) {
            navigate(routes.dashboard);
        }
        
        const savedEmail = localStorage.getItem('remembered_email');
        if (savedEmail) {
            setFormData(prev => ({ ...prev, email: savedEmail }));
            setRememberMe(true);
        }
    }, [navigate, routes.dashboard]);
    
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data);
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.email || !formData.password) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please enter email and password',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }
        
        setLoading(true);
        
        try {
            const result = await authActions.login(formData);
            
            if (result.success) {
                if (rememberMe) {
                    localStorage.setItem('remembered_email', formData.email);
                } else {
                    localStorage.removeItem('remembered_email');
                }
                
                MySwal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Login successful! Redirecting...',
                    timer: 1500,
                    showConfirmButton: false
                }).then(() => {
                    navigate('/');
                });
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Login Failed',
                    text: result.message || 'Invalid email or password',
                    timer: 3000,
                    showConfirmButton: true
                });
            }
        } catch (error) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred. Please try again.',
                timer: 3000,
                showConfirmButton: true
            });
        } finally {
            setLoading(false);
        }
    };
    
    // Quick fill demo credentials
    const fillDemoCredentials = (role) => {
        if (role === 'admin') {
            setFormData({
                email: 'admin@company.com',
                password: 'Admin@123'
            });
        } else {
            setFormData({
                email: 'employee@company.com',
                password: 'Employee@123'
            });
        }
    };
    
    return (
        <div className="login-container">
            <div className="login-box">
                {/* Left Side - Branding */}
                <div className="login-left">
                    <div className="brand-content">
                        <div className="brand-logo">
                            {companySettings.company_logo ? (
                                <img 
                                    src={`/${companySettings.company_logo}`} 
                                    alt={companySettings.company_name}
                                />
                            ) : (
                                <div className="default-logo">
                                    <Shield size={48} />
                                </div>
                            )}
                        </div>
                        <h1 className="brand-title">{companySettings.company_name || 'Company Name'}</h1>
                        <p className="brand-subtitle">Welcome back! Please sign in to continue.</p>
                        
                        <div className="brand-features">
                            <div className="feature-item">
                                <div className="feature-icon">
                                    <User size={20} />
                                </div>
                                <div className="feature-text">
                                    <h4>Employee Management</h4>
                                    <p>Manage your team efficiently</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-icon">
                                    <Lock size={20} />
                                </div>
                                <div className="feature-text">
                                    <h4>Secure Access</h4>
                                    <p>Your data is protected</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right Side - Login Form */}
                <div className="login-right">
                    <div className="form-container">
                        <div className="form-header">
                            <h2>Sign In</h2>
                            <p>Access your account using your credentials</p>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="login-form">
                            {/* Email Field */}
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <div className="input-wrapper">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="form-input"
                                        placeholder="Enter your email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        required
                                    />
                                    <Mail className="input-icon" size={20} />
                                </div>
                            </div>
                            
                            {/* Password Field */}
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        className="form-input"
                                        placeholder="Enter your password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        required
                                    />
                                    <Lock className="input-icon" size={20} />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>
                            
                            {/* Remember Me & Forgot Password */}
                            <div className="form-options">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <span>Remember me</span>
                                </label>
                                
                                <Link to={routes.forgotPassword} className="forgot-link">
                                    Forgot password?
                                </Link>
                            </div>
                            
                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Signing in...
                                    </>
                                ) : (
                                    <>
                                        <LogIn size={20} />
                                        Sign In
                                    </>
                                )}
                            </button>
                        </form>
                        
                        {/* Register Link */}
                        <div className="register-section">
                            <p>
                                Dont have an account?{' '}
                                <Link to={routes.register} className="register-link">
                                    Create Account
                                </Link>
                            </p>
                        </div>
                        
                        {/* Demo Credentials */}
                        <div className="demo-section">
                            <h3>Demo Credentials</h3>
                            <div className="demo-buttons">
                                <button 
                                    className="demo-button admin"
                                    onClick={() => fillDemoCredentials('admin')}
                                    type="button"
                                >
                                    <Shield size={16} />
                                    <div className="demo-info">
                                        <span className="demo-role">Admin</span>
                                        <span className="demo-details">admin@company.com • Admin@123</span>
                                    </div>
                                </button>
                                
                                <button 
                                    className="demo-button employee"
                                    onClick={() => fillDemoCredentials('employee')}
                                    type="button"
                                >
                                    <User size={16} />
                                    <div className="demo-info">
                                        <span className="demo-role">Employee</span>
                                        <span className="demo-details">employee@company.com • Employee@123</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;